import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

export default function WidgetContextDocs() {
  const { clientId } = useParams();
  return (
    <>
      <SEO title={clientId ? `Page Context Setup — ${clientId}` : "Widget Page Context Setup"} noIndex />
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Navbar />
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "40px 20px", fontFamily: "sans-serif" }}>
        <div style={{ background: "#fff", padding: "32px", borderRadius: "16px", boxShadow: "0 8px 30px rgba(0,0,0,.08)" }}>
          <h1 style={{ fontFamily: "var(--font-display)", marginTop: 0 }}>Widget Page Context Setup</h1>
          <p style={{ color: "#555", lineHeight: 1.7 }}>
            Your widget can understand <strong>which page</strong> a visitor is on, <strong>what product</strong> they are viewing, and <strong>who they are</strong> (if logged in).
            This lets the AI give accurate, personalized answers without any extra backend work.
          </p>

          <h2>How it works</h2>
          <p>On the host website, set a global <code>window.tredexWidget.context</code> object before or during chat.
            The widget reads this object on every message and sends it to the AI.</p>

          <pre style={{ background: "#f6f7fb", padding: "16px", borderRadius: "12px", overflow: "auto" }}>{`<script>
  window.tredexWidget = window.tredexWidget || {}
  window.tredexWidget.context = {
    pageUrl: window.location.href,
    pageTitle: document.title,
    product: {
      id: 'SKU-123',
      name: 'Blue Lens Glasses',
      price: '$49',
      category: 'Eyewear',
      sku: 'SKU-123'
    },
    user: {
      id: 'u_456',
      email: 'john@example.com',
      name: 'John Doe',
      loggedIn: true
    }
  }
</script>
<script src="https://tredextechnology.in/embed.js" data-client="YOUR_CLIENT_ID" data-color="#8B5E3C"></script>`}</pre>

          <h2>Field Reference</h2>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#f6f7fb" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", border: "1px solid #eee" }}>Field</th>
                <th style={{ textAlign: "left", padding: "10px 12px", border: "1px solid #eee" }}>Type</th>
                <th style={{ textAlign: "left", padding: "10px 12px", border: "1px solid #eee" }}>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>pageUrl</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Current page URL</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>pageTitle</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Current page title</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>product.id</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Product ID / SKU</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>product.name</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Product name</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>product.price</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Price string</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>product.category</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Category</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>product.sku</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Stock keeping unit</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>user.id</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>Internal user ID</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>user.email</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>User email address</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>user.name</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>string</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>User display name</td></tr>
              <tr><td style={{ padding: "10px 12px", border: "1px solid #eee" }}><code>user.loggedIn</code></td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>boolean</td><td style={{ padding: "10px 12px", border: "1px solid #eee" }}>true if the user is logged in</td></tr>
            </tbody>
          </table>

          <h2>SPA note</h2>
          <p>For single-page apps, update <code>window.tredexWidget.context</code> on route changes so the AI always knows the current page/product.</p>

          <h2>What the business sees</h2>
          <p>In the admin panel, every question now shows page URL, user info, and product context so you can identify high-intent visitors.</p>
        </div>
      </div>
      <Footer />
    </>
  );
}
